import { makeVar } from '@apollo/client';
import { useReactiveVar } from '@apollo/client/react';
import { useEffect } from 'react';

import { fetchOpenReviewReportsCount, isModerationForbiddenError } from './review-moderation.api';

export type ReviewModerationAccess = 'unknown' | 'moderator' | 'forbidden';

export const reviewModerationAccessVar = makeVar<ReviewModerationAccess>('unknown');
export const openReviewReportsCountVar = makeVar<number | null>(null);

export function recordOpenReviewReportsCount(count: number) {
  reviewModerationAccessVar('moderator');
  openReviewReportsCountVar(count);
}

export function recordReviewModerationForbidden() {
  reviewModerationAccessVar('forbidden');
  openReviewReportsCountVar(null);
}

// Asks the API once per sign-in whether this Account moderates, and how many Review reports wait.
export function useReviewModerationAccess(isAuthenticated: boolean, accessToken?: string | null) {
  const access = useReactiveVar(reviewModerationAccessVar);
  const openReportsCount = useReactiveVar(openReviewReportsCountVar);

  useEffect(() => {
    if (!isAuthenticated) {
      reviewModerationAccessVar('unknown');
      openReviewReportsCountVar(null);
      return;
    }

    let isCancelled = false;
    fetchOpenReviewReportsCount(accessToken ?? undefined)
      .then((count) => {
        if (!isCancelled) {
          recordOpenReviewReportsCount(count);
        }
      })
      .catch((error) => {
        if (!isCancelled && isModerationForbiddenError(error)) {
          recordReviewModerationForbidden();
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [accessToken, isAuthenticated]);

  return { access, openReportsCount };
}
